import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Loader2, Check, Sparkles, Copy, ExternalLink, RefreshCw, Link2, AlertCircle, CheckCircle2, ImageOff, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { MindMapNode, Notebook } from '../types';
import { generateSocialPost, getSocialConnections, socialOAuthInit, openOAuthPopup, publishSocialPost, disconnectSocial, resolveNoteLink, intentUrl, SocialConnections, SocialPlatform, scoreNoteForThreads } from '../services/socialService';
import { storage } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface SocialPostModalProps {
  node: MindMapNode;
  notebook: Notebook;
  language: string;
  onClose: () => void;
  initialPostText?: string;
  initialHashtags?: string[];
}

type Platform = SocialPlatform;
type Tone = 'default' | 'shorter' | 'technical' | 'beginner';

const IMAGE_LIMITS: Record<Platform, number> = { threads: 10, linkedin: 9, x: 4 };

const PLATFORM_META = {
  threads: {
    label: 'Threads',
    charLimit: 500,
    icon: (
      <svg viewBox="0 0 192 192" fill="currentColor" className="w-4 h-4">
        <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.111 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.352-22.809-.169-40.06-7.484-51.275-21.741C35.236 139.966 29.808 120.682 29.605 96c.203-24.682 5.63-43.966 16.133-57.317C56.954 24.426 74.204 17.11 97.013 16.94c22.975.17 40.526 7.52 52.171 21.847 5.71 7.026 10.015 15.86 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C147.036 9.607 125.202.195 97.07 0h-.113C68.882.195 47.292 9.643 32.788 28.08 19.882 44.485 13.224 67.315 13.001 95.932L13 96v.067c.224 28.617 6.882 51.447 19.788 67.854C47.292 182.358 68.882 191.806 96.957 192h.113c24.96-.173 42.554-6.708 57.048-21.189 18.963-18.945 18.392-42.692 12.142-57.27-4.484-10.454-13.033-18.945-24.723-24.553Zm-43.099 43.051c-10.426.588-21.258-4.098-26.87-11.726-3.595-4.833-5.062-10.563-4.08-16.585 1.644-10.087 11.382-16.537 27.197-17.45 2.375-.136 4.69-.202 6.952-.202 5.807 0 11.282.526 16.475 1.542-1.87 23.317-11.417 43.181-19.674 44.421Z"/>
      </svg>
    ),
  },
  linkedin: {
    label: 'LinkedIn',
    charLimit: 3000,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
  x: {
    label: 'X / Twitter',
    charLimit: 280,
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.258 5.624 5.906-5.624zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
} as const;

function MakeItPostworthyTip({ tip, isVi }: { tip: string; isVi: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(o => !o)}
      className="flex-1 min-w-[180px] text-left rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/30"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Zap size={11} className="text-amber-500 shrink-0" />
          <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
            {isVi ? 'Làm cho dễ post hơn' : 'Make it postworthy'}
          </span>
        </div>
        {open ? <ChevronUp size={11} className="text-amber-500 shrink-0" /> : <ChevronDown size={11} className="text-amber-500 shrink-0" />}
      </div>
      {open && (
        <p className="mt-1.5 text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">{tip}</p>
      )}
    </button>
  );
}

export function SocialPostModal({ node, notebook, language, onClose, initialPostText, initialHashtags }: SocialPostModalProps) {
  const isVi = language === 'vietnamese';
  const [platform, setPlatform] = useState<Platform>('threads');
  const [postText, setPostText] = useState(initialPostText || '');
  const [hashtags, setHashtags] = useState<string[]>(initialHashtags || []);
  const [tone, setTone] = useState<Tone>('default');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showAiPanel, setShowAiPanel] = useState(!initialPostText);

  const [connections, setConnections] = useState<SocialConnections>({ x: null, threads: null, linkedin: null });
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [connecting, setConnecting] = useState<Platform | null>(null);
  const [disconnecting, setDisconnecting] = useState<Platform | null>(null);

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishDone, setPublishDone] = useState<{ postUrl: string } | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  const [watermarkLabel, setWatermarkLabel] = useState(notebook.title);
  const [localMakeIt, setLocalMakeIt] = useState<string | null>(node.makeItPostworthy || node.analysis?.makeItPostworthy || null);
  const [isScoringMakeIt, setIsScoringMakeIt] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>(() => {
    const img = node.imageBase64;
    if (img) {
      const src = img.startsWith('http') || img.startsWith('data:') ? img : `data:${node.imageMimeType || 'image/jpeg'};base64,${img}`;
      return [src];
    }
    const att = node.attachments?.find(a => a.type === 'image');
    if (att?.content) {
      const src = att.content.startsWith('http') || att.content.startsWith('data:') ? att.content : `data:${att.mimeType};base64,${att.content}`;
      return [src];
    }
    return [];
  });
  const [copied, setCopied] = useState(false);
  const [postLang, setPostLang] = useState<string>(language === 'vietnamese' ? 'vietnamese' : 'english');
  const [showLink, setShowLink] = useState(true);
  const [linkInFirstComment, setLinkInFirstComment] = useState(false);
  const [threadsTopic, setThreadsTopic] = useState('');

  // Find L1 ancestor (direct child of root) to get the correct .md filename
  const l1Ancestor = (() => {
    const nodeMap = new Map(notebook.nodes.map(n => [n.id, n]));
    const rootId = notebook.nodes.find(n => !n.parentId)?.id;
    let cur = nodeMap.get(node.id);
    while (cur && cur.parentId && cur.parentId !== rootId) {
      cur = nodeMap.get(cur.parentId);
    }
    return cur ?? node;
  })();
  const noteLink = resolveNoteLink(notebook.lastPublishUrl, l1Ancestor.title, node.id);
  const charLimit = PLATFORM_META[platform].charLimit;
  const hashtagLine = hashtags.length ? '\n\n' + hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ') : '';
  const attachedLink = showLink && noteLink && !linkInFirstComment ? `\n\n${noteLink}` : '';
  const firstCommentLine = linkInFirstComment && noteLink ? (isVi ? '\n\nFull note ở comment đầu.' : '\n\nFull note in first comment.') : '';
  const fullPost = `${postText}${attachedLink}${firstCommentLine}${hashtagLine}`;
  const charCount = fullPost.length;
  const isOverLimit = charCount > charLimit;

  const allImages = useMemo(() => {
    const result: { src: string; key: string; nodeTitle: string }[] = [];
    for (const n of notebook.nodes) {
      if (n.imageBase64) {
        const img = n.imageBase64;
        const src = img.startsWith('http') || img.startsWith('data:') ? img : `data:${n.imageMimeType || 'image/jpeg'};base64,${img}`;
        result.push({ src, key: `${n.id}_main`, nodeTitle: n.title || '' });
      }
      for (let i = 0; i < (n.attachments?.length ?? 0); i++) {
        const att = n.attachments![i];
        if (att.type === 'image' && att.content) {
          const src = att.content.startsWith('http') || att.content.startsWith('data:') ? att.content : `data:${att.mimeType};base64,${att.content}`;
          result.push({ src, key: `${n.id}_att${i}`, nodeTitle: n.title || '' });
        }
      }
    }
    return result;
  }, [notebook.nodes]);

  // Load connection status
  useEffect(() => {
    getSocialConnections().then(setConnections).catch(() => {}).finally(() => setLoadingConnections(false));
  }, []);

  const handleGenerate = useCallback(async (t: Tone = tone) => {
    setIsGenerating(true);
    setGenerateError(null);
    setPublishDone(null);
    try {
      const result = await generateSocialPost(node.title || '', node.content || '', postLang, platform, noteLink || '', t);
      const tags = (result.hashtags || []).slice(0, 3).map((h: string) => h.startsWith('#') ? h : `#${h}`);
      const tagLine = tags.length ? '\n\n' + tags.map((h: string) => h.startsWith('#') ? h : `#${h}`).join(' ') : '';
      const limit = PLATFORM_META[platform].charLimit;
      let post = result.post;
      if (post.length + tagLine.length > limit) {
        const linkMatch = post.match(/\n*Full note:.*$/s);
        const linkPart = linkMatch ? linkMatch[0] : '';
        const bodyPart = linkPart ? post.slice(0, post.length - linkPart.length) : post;
        const budget = limit - tagLine.length - linkPart.length;
        const trimmedBody = bodyPart.slice(0, budget).replace(/\s+\S*$/, '').trimEnd();
        post = trimmedBody + linkPart;
      }
      setPostText(post);
      setHashtags(tags);
    } catch (e: any) {
      setGenerateError(e.message || 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  }, [node, postLang, platform, noteLink, tone]);

  // Auto-generate on open
  const handleConnect = async (p: Platform) => {
    setConnecting(p);
    try {
      const { authUrl } = await socialOAuthInit(p);
      const result = await openOAuthPopup(authUrl);
      setConnections(prev => ({ ...prev, [result.platform]: { username: result.username } }));
    } catch (e: any) {
      alert(e.message || 'Connection failed');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (p: Platform) => {
    setDisconnecting(p);
    try {
      await disconnectSocial(p);
      setConnections(prev => ({ ...prev, [p]: null }));
    } finally {
      setDisconnecting(null);
    }
  };

  const uploadImageWithWatermark = useCallback(async (imgSrc: string, index: number): Promise<string> => {
    const blob = await new Promise<Blob>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const MAX = 1080;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d')!;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const fontSize = Math.max(11, Math.round(w * 0.022));
        ctx.font = `${fontSize}px system-ui, sans-serif`;
        const label = watermarkLabel ? `📖 ${watermarkLabel}` : '';
        if (label) {
          const textW = ctx.measureText(label).width;
          const pad = 8;
          ctx.fillStyle = 'rgba(0,0,0,0.45)';
          ctx.fillRect(w - textW - pad * 2, h - fontSize - pad * 2, textW + pad * 2, fontSize + pad * 1.5);
          ctx.fillStyle = 'rgba(255,255,255,0.88)';
          ctx.textBaseline = 'top';
          ctx.fillText(label, w - textW - pad, h - fontSize - pad);
        }
        c.toBlob(b => b ? resolve(b) : reject(new Error('canvas blob failed')), 'image/jpeg', 0.85);
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = imgSrc;
    });
    const storageRef = ref(storage, `social_temp/${Date.now()}_${index}.jpg`);
    await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
    return getDownloadURL(storageRef);
  }, [watermarkLabel]);

  const handlePublishDirect = async () => {
    if (!connections[platform]) { alert(isVi ? 'Chưa kết nối tài khoản.' : 'Account not connected.'); return; }
    setIsPublishing(true);
    setPublishError(null);
    setPublishDone(null);
    try {
      const uploadedUrls: string[] = [];
      for (let i = 0; i < selectedImages.length; i++) {
        const url = await uploadImageWithWatermark(selectedImages[i], i);
        uploadedUrls.push(url);
      }
      const result = await publishSocialPost(platform, fullPost, uploadedUrls.length > 0 ? uploadedUrls : undefined, platform === 'threads' && threadsTopic ? threadsTopic : undefined);
      setPublishDone(result);
    } catch (e: any) {
      setPublishError(e.message || 'Publish failed');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleOpenIntent = () => {
    window.open(intentUrl(platform, fullPost), '_blank', 'noopener');
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullPost);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const connected = connections[platform];

  return (
    <>
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[860px] flex flex-col overflow-hidden max-h-[94vh]">

        {/* Header */}
        <div className="px-6 py-4 border-b dark:border-slate-700 shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900 dark:text-white text-base">
              {isVi ? 'Đăng lên mạng xã hội' : 'Publish to social'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg text-gray-500">
              <X size={18} />
            </button>
          </div>

          {/* 3-score strip */}
          {(() => {
            const postworthy = node.analysis?.postworthyScore ?? node.threadsScore ?? null;
            const correctness = node.correctnessScore ?? null;
            const aiCheck = node.analysis?.score ?? null;
            const hasAny = postworthy != null || correctness != null || aiCheck != null;
            if (!hasAny) return null;
            const scoreColor = (s: number) => s >= 80 ? 'text-emerald-600 dark:text-emerald-400' : s >= 60 ? 'text-amber-500 dark:text-amber-400' : 'text-red-500 dark:text-red-400';
            const ScorePill = ({ label, score, sub }: { label: string; score: number | null; sub?: string }) => (
              <div className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 min-w-[64px]">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none">{label}</span>
                {score != null
                  ? <span className={`text-xl font-bold font-mono leading-tight ${scoreColor(score)}`}>{score}</span>
                  : <span className="text-base text-gray-300 dark:text-gray-600 leading-tight">—</span>}
                {sub && <span className="text-[8px] text-gray-300 dark:text-gray-600 leading-none">{sub}</span>}
              </div>
            );
            const handleGetAdvice = async () => {
              setIsScoringMakeIt(true);
              try {
                const result = await scoreNoteForThreads({ notebookId: notebook.id, nodeId: node.id, title: node.title || '', content: node.content || '', language });
                setLocalMakeIt(result.makeItPostworthy || null);
              } catch { /* ignore */ } finally {
                setIsScoringMakeIt(false);
              }
            };
            return (
              <div className="mt-3 flex items-start gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <ScorePill label="Postworthy" score={postworthy} sub="/100" />
                  <ScorePill label="Correctness" score={correctness} sub="/100" />
                  <ScorePill label="AI Check" score={aiCheck} sub="/100" />
                </div>
                {localMakeIt ? (
                  <MakeItPostworthyTip tip={localMakeIt} isVi={isVi} />
                ) : (
                  <button
                    onClick={handleGetAdvice}
                    disabled={isScoringMakeIt}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-60"
                  >
                    {isScoringMakeIt ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                    {isVi ? 'Tư vấn cách cải thiện' : 'Get postworthy advice'}
                  </button>
                )}
              </div>
            );
          })()}
        </div>

        <div className="flex overflow-hidden flex-1 min-h-0">

          {/* Left: post editor */}
          <div className="flex-1 flex flex-col gap-4 p-5 overflow-y-auto min-w-0 bg-gray-50 dark:bg-slate-950">

            {/* Platform tabs */}
            <div className="flex gap-2">
              {(['threads', 'linkedin'] as Platform[]).map(p => (
                <button
                  key={p}
                  onClick={() => { setPlatform(p); setPublishDone(null); setPublishError(null); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-bold text-sm transition-colors ${platform === p ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-gray-400'}`}
                >
                  {PLATFORM_META[p].icon}
                  {PLATFORM_META[p].label}
                </button>
              ))}
            </div>

            {/* Post editor */}
            <div className="flex flex-col gap-1.5 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{isVi ? 'Nội dung bài đăng' : 'Post content'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{isVi ? 'Viết điều bạn muốn chia sẻ, hoặc nhờ AI →' : 'Write your take, or let AI help →'}</p>
                </div>
                <span className={`text-[11px] font-mono font-bold ${isOverLimit ? 'text-red-500' : charCount > charLimit * 0.85 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {charCount} / {charLimit}
                </span>
              </div>
              <textarea
                value={postText}
                onChange={e => setPostText(e.target.value)}
                rows={9}
                className={`w-full text-sm text-gray-800 dark:text-gray-100 bg-white dark:bg-slate-800 border rounded-xl p-3 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-600 leading-relaxed ${isOverLimit ? 'border-red-300 dark:border-red-600' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder={isVi ? 'Chia sẻ insight của bạn về note này...' : 'Share your insight about this note...'}
                disabled={isGenerating}
              />
              {isOverLimit && (
                <p className="text-[11px] text-red-500">
                  {isVi ? `Quá ${charLimit} ký tự — chọn "Ngắn hơn" để AI rút gọn.` : `Over ${charLimit} chars — choose "Shorter" to trim.`}
                </p>
              )}
            </div>

            {/* Hashtags */}
            {hashtags.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Hashtags</p>
                <div className="flex flex-wrap gap-1.5">
                  {hashtags.map(tag => (
                    <span key={tag} className="group inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-xs font-semibold rounded-full border border-indigo-200 dark:border-indigo-700/50">
                      {tag}
                      <button onClick={() => setHashtags(h => h.filter(t => t !== tag))} className="opacity-40 group-hover:opacity-100 hover:text-red-500 transition-all"><X size={9} /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Threads topic */}
            {platform === 'threads' && (
              <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest shrink-0">{isVi ? 'Topic' : 'Topic'}</span>
                <input
                  type="text"
                  value={threadsTopic}
                  onChange={e => setThreadsTopic(e.target.value.replace(/^#/, ''))}
                  placeholder={isVi ? 'VD: LearningInPublic (không cần #)' : 'e.g. LearningInPublic (no #)'}
                  className="flex-1 text-xs bg-transparent text-gray-700 dark:text-gray-300 focus:outline-none placeholder:text-gray-300 dark:placeholder:text-slate-600"
                />
              </div>
            )}

            {/* Link attachment */}
            <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-colors ${
              noteLink
                ? (showLink || linkInFirstComment
                    ? 'bg-indigo-50/60 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700/50'
                    : 'bg-gray-50 dark:bg-slate-800/40 border-gray-200 dark:border-slate-700')
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/50'
            }`}>
              <Link2 size={13} className={`shrink-0 ${noteLink ? (showLink || linkInFirstComment ? 'text-indigo-400' : 'text-gray-400') : 'text-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                  {isVi ? 'Link full note' : 'Full note link'}
                  {noteLink && !linkInFirstComment && showLink && <span className="ml-1 font-normal text-gray-400">{isVi ? '— tự động đính kèm' : '— auto-attached'}</span>}
                  {noteLink && linkInFirstComment && <span className="ml-1 font-normal text-indigo-400">{isVi ? '— ở comment đầu' : '— in first comment'}</span>}
                </p>
                {noteLink ? (
                  <a href={noteLink} target="_blank" rel="noopener" className="text-[11px] text-indigo-500 truncate block hover:underline mt-0.5">{noteLink}</a>
                ) : (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">{isVi ? 'Chưa publish lên GitHub' : 'Not published to GitHub yet'}</p>
                )}
              </div>
              {noteLink && (
                <div className="flex items-center gap-1 shrink-0">
                  {/* Link-in-first-comment toggle */}
                  <button
                    onClick={() => { setLinkInFirstComment(v => !v); if (!showLink) setShowLink(true); }}
                    title={isVi ? 'Đặt link ở comment đầu thay vì bài đăng' : 'Put link in first comment instead of post body'}
                    className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border transition-colors ${
                      linkInFirstComment
                        ? 'bg-violet-500 text-white border-violet-500'
                        : 'border-gray-300 dark:border-slate-600 text-gray-400 hover:border-violet-400 hover:text-violet-400'
                    }`}
                  >
                    {isVi ? '1st cmt' : '1st cmt'}
                  </button>
                  {/* Remove / restore toggle */}
                  {showLink && !linkInFirstComment && (
                    <button
                      onClick={() => setShowLink(false)}
                      title={isVi ? 'Xóa link khỏi bài đăng' : 'Remove link from post'}
                      className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  )}
                  {!showLink && !linkInFirstComment && (
                    <button
                      onClick={() => setShowLink(true)}
                      className="text-[10px] text-indigo-500 hover:underline"
                    >
                      {isVi ? 'Khôi phục' : 'Restore'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* First-comment box */}
            {linkInFirstComment && noteLink && (
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl border border-violet-200 dark:border-violet-700/50 bg-violet-50/60 dark:bg-violet-900/10">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-violet-700 dark:text-violet-400">
                    {isVi ? 'Comment đầu tiên (paste sau khi đăng)' : 'First comment (paste after posting)'}
                  </p>
                  <p className="text-[11px] text-violet-600 dark:text-violet-300 mt-0.5 break-all">{noteLink}</p>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(noteLink); }}
                  className="shrink-0 flex items-center gap-1 text-[10px] text-violet-600 dark:text-violet-400 hover:underline font-medium mt-0.5"
                >
                  <Copy size={10} /> Copy
                </button>
              </div>
            )}

            {/* Image picker from all nodes — multi-select */}
            {allImages.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{isVi ? 'Ảnh đính kèm' : 'Attach images'}</p>
                  <span className="text-[10px] text-gray-400">
                    {selectedImages.length}/{IMAGE_LIMITS[platform]} {isVi ? 'ảnh' : 'images'}
                  </span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {allImages.map(({ src, key, nodeTitle }) => {
                    const isSelected = selectedImages.includes(src);
                    const atLimit = selectedImages.length >= IMAGE_LIMITS[platform];
                    const disabled = !isSelected && atLimit;
                    const selectedIndex = isSelected ? selectedImages.indexOf(src) + 1 : null;
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedImages(prev => prev.filter(s => s !== src));
                          } else if (!atLimit) {
                            setSelectedImages(prev => [...prev, src]);
                          }
                        }}
                        disabled={disabled}
                        className={`relative shrink-0 rounded-xl border-2 overflow-hidden transition-all ${isSelected ? 'border-indigo-500 ring-2 ring-indigo-300 dark:ring-indigo-600' : disabled ? 'border-gray-100 dark:border-slate-800 opacity-40 cursor-not-allowed' : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300'}`}
                        style={{ width: 160, height: 120 }}
                      >
                        <img src={src} alt={nodeTitle} className="w-full h-full object-contain bg-white dark:bg-slate-800" />
                        {selectedIndex !== null && (
                          <div className="absolute top-1.5 right-1.5">
                            <span className="flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-indigo-500 rounded-full shadow">
                              {selectedIndex}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedImages.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1.5">
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 leading-snug">
                      {isVi ? '⚠️ Ảnh từ sách có thể có copyright. Chỉ dùng cho mục đích học thuật.' : '⚠️ Textbook screenshots may be copyrighted. Educational use only.'}
                    </p>
                    <div>
                      <p className="text-[10px] text-gray-400 mb-1">{isVi ? 'Nhãn watermark' : 'Watermark label'}</p>
                      <input
                        type="text"
                        value={watermarkLabel}
                        onChange={e => setWatermarkLabel(e.target.value)}
                        placeholder={isVi ? 'Để trống = không có watermark' : 'Leave empty = no watermark'}
                        className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-600"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: controls */}
          <div className="w-60 shrink-0 border-l dark:border-slate-700 p-5 flex flex-col gap-4 bg-white dark:bg-slate-900 overflow-y-auto">

            {/* Generate */}
            <div>
              <button
                onClick={() => setShowAiPanel(v => !v)}
                className="flex items-center justify-between w-full group mb-2"
              >
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">AI Generate</p>
                <span className={`text-[10px] text-gray-300 dark:text-gray-600 transition-transform ${showAiPanel ? '' : 'rotate-180'}`}>▲</span>
              </button>

              {showAiPanel && <>
              {/* Language + Tone selects */}
              <div className="flex flex-col gap-1.5 mb-3">
                <select
                  value={postLang}
                  onChange={e => setPostLang(e.target.value)}
                  className="w-full text-xs px-2.5 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                >
                  <option value="vietnamese">🇻🇳 Tiếng Việt</option>
                  <option value="english">🇬🇧 English</option>
                  <option value="bilingual">🌐 VI + EN</option>
                  <option value="french">🇫🇷 Français</option>
                  <option value="spanish">🇪🇸 Español</option>
                  <option value="german">🇩🇪 Deutsch</option>
                  <option value="japanese">🇯🇵 日本語</option>
                  <option value="korean">🇰🇷 한국어</option>
                </select>
                <select
                  value={tone}
                  onChange={e => setTone(e.target.value as Tone)}
                  className="w-full text-xs px-2.5 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                >
                  <option value="default">{isVi ? '✨ Mặc định' : '✨ Default'}</option>
                  <option value="shorter">{isVi ? '✂️ Ngắn hơn' : '✂️ Shorter'}</option>
                  <option value="technical">{isVi ? '🔬 Kỹ thuật' : '🔬 Technical'}</option>
                  <option value="beginner">{isVi ? '🎯 Đơn giản' : '🎯 Beginner'}</option>
                </select>
              </div>

              <button
                onClick={() => handleGenerate(tone)}
                disabled={isGenerating}
                className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-xs transition-colors disabled:opacity-60 shadow-md shadow-violet-300 dark:shadow-violet-900/30"
              >
                {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {isGenerating ? (isVi ? 'Đang tạo...' : 'Generating...') : (isVi ? '✨ Tạo bài đăng' : '✨ Generate post')}
              </button>
              {generateError && <p className="text-[11px] text-red-500 mt-1 text-center">{generateError}</p>}
              </>}
            </div>

            <div className="h-px bg-gray-100 dark:bg-slate-700" />

            {/* Connected account */}
            <div>
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5">
                {isVi ? 'Tài khoản' : 'Account'}
              </p>
              {loadingConnections ? (
                <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={12} className="animate-spin" /> Loading…</div>
              ) : connected ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                    <span className="text-xs font-bold text-green-700 dark:text-green-300 truncate">@{connected.username}</span>
                  </div>
                  <button
                    onClick={() => handleDisconnect(platform)}
                    disabled={disconnecting === platform}
                    className="text-[10px] text-gray-400 hover:text-red-500 transition-colors shrink-0"
                  >
                    {disconnecting === platform ? <Loader2 size={10} className="animate-spin" /> : isVi ? 'Ngắt' : 'Disconnect'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleConnect(platform)}
                  disabled={connecting === platform}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-bold text-xs transition-colors disabled:opacity-60"
                >
                  {connecting === platform ? <Loader2 size={13} className="animate-spin" /> : PLATFORM_META[platform].icon}
                  {connecting === platform
                    ? (isVi ? 'Đang kết nối...' : 'Connecting...')
                    : (isVi ? `Kết nối ${PLATFORM_META[platform].label}` : `Connect ${PLATFORM_META[platform].label}`)}
                </button>
              )}
            </div>

            <div className="h-px bg-gray-100 dark:bg-slate-700" />

            {/* Publish actions */}
            <div className="flex flex-col gap-2 mt-auto">

              {/* Success state */}
              {publishDone && (
                <div className="flex flex-col gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-700">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-xs font-bold">
                    <CheckCircle2 size={14} /> {isVi ? 'Đã đăng thành công!' : 'Published!'}
                  </div>
                  <a href={publishDone.postUrl} target="_blank" rel="noopener" className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline truncate">
                    <ExternalLink size={11} /> {publishDone.postUrl}
                  </a>
                </div>
              )}

              {publishError && (
                <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-700">
                  <AlertCircle size={13} className="text-red-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-600 dark:text-red-400">{publishError}</p>
                </div>
              )}

              {/* Primary: Direct publish (if connected) */}
              {connected ? (
                <button
                  onClick={handlePublishDirect}
                  disabled={isPublishing || isOverLimit || !postText.trim()}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-bold text-sm transition-colors disabled:opacity-60 shadow-lg"
                >
                  {isPublishing ? <Loader2 size={14} className="animate-spin" /> : publishDone ? <Check size={14} /> : PLATFORM_META[platform].icon}
                  {isPublishing ? (isVi ? 'Đang đăng...' : 'Publishing...') : publishDone ? (isVi ? 'Đăng lại' : 'Post again') : (isVi ? `Đăng lên ${PLATFORM_META[platform].label}` : `Publish to ${PLATFORM_META[platform].label}`)}
                </button>
              ) : (
                /* Fallback: open intent URL (text pre-filled, user posts) */
                <button
                  onClick={handleOpenIntent}
                  disabled={!postText.trim()}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-bold text-sm transition-colors disabled:opacity-60 shadow-lg"
                >
                  <ExternalLink size={14} />
                  {isVi ? `Mở ${PLATFORM_META[platform].label} (text điền sẵn)` : `Open ${PLATFORM_META[platform].label} (text pre-filled)`}
                </button>
              )}

              {/* Copy fallback */}
              <button
                onClick={handleCopy}
                disabled={!postText.trim()}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold text-sm transition-colors"
              >
                {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                {copied ? (isVi ? 'Đã copy!' : 'Copied!') : (isVi ? 'Copy bài đăng' : 'Copy post')}
              </button>

              {/* Intent URL note for connected users */}
              {connected && !publishDone && (
                <button onClick={handleOpenIntent} className="text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-center underline underline-offset-2 transition-colors">
                  {isVi ? `Hoặc mở ${PLATFORM_META[platform].label} thủ công ↗` : `Or open ${PLATFORM_META[platform].label} manually ↗`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

</>
  );
}
